import { HomeIcon } from "@heroicons/react/outline";
import { PlusIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useParams } from "react-router-dom";

import { EmptyState } from "./EmptyState";
import { FavoritesSection } from "./FavoritesSection";
import { HomeRow } from "./HomeRow";
import { TrailingDropZone } from "./TrailingDropZone";
import { VirtualizedTreeItem } from "./VirtualizedTreeItem";
import { useFileHierarchy } from "./hooks/useFileHierarchy";
import { useSidebarSelectionKeyboard } from "./hooks/useSidebarSelectionKeyboard";
import { useTreeNavigation } from "./hooks/useTreeNavigation";
import { useVirtualizedTree } from "./hooks/useVirtualizedTree";
import { FileHierarchyProps } from "./types";
import { getOffsetRelativeToAncestor } from "./utils/getOffsetRelativeToAncestor";

import LoadingSpinner from "@/components/LoadingSpinner";
import SmartTableCreateDialog from "@/components/SmartTableCreateDialog";
import { DragDropProvider } from "@/components/UnifiedRegistry/DragAndDrop/DragDropProvider";
import NewItemPopoverContent from "@/components/UnifiedRegistry/NewItemButton/NewItemPopoverContent";
import useNewItemActions from "@/components/UnifiedRegistry/NewItemButton/hooks/useNewItemActions";
import {
  ITEM_TYPE_GROUPS,
  NewItemEntityType,
} from "@/components/UnifiedRegistry/NewItemButton/new-item-types";
import { RegistryItem } from "@/components/UnifiedRegistry/types";
import {
  UnifiedRegistryProvider,
  useUnifiedRegistry,
} from "@/components/UnifiedRegistry/unified-registry-context";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { useLocalStorageState } from "@/hooks";
import { LOCAL_STORAGE_KEYS, SIDEBAR_FAVORITES_EXPAND_EVENT } from "@/hooks/useLocalStorageState";
import { cn } from "@/lib/utils";
import { useRbac } from "@/providers/RbacProvider";
import { useMoveFolderEntity } from "@/queries/folder-entities";
import { Entity, EntityType, getEntityNumericId } from "@/queries/folder-entities/types";
import { displayToast } from "@/utils/toast";

const HierarchyLoadingState = () => (
  <div className="space-y-2 py-2">
    <Skeleton className="h-8 w-full rounded-md" />
    <Skeleton className="ml-4 h-8 w-11/12 rounded-md" />
    <Skeleton className="ml-4 h-8 w-10/12 rounded-md" />
    <Skeleton className="h-8 w-full rounded-md" />
    <Skeleton className="ml-4 h-8 w-11/12 rounded-md" />
  </div>
);

interface HierarchyContentProps {
  isLoading: boolean;
  hasItems: boolean;
  children: ReactNode;
}

const HierarchyContent = ({ isLoading, hasItems, children }: HierarchyContentProps) => {
  if (isLoading) {
    return <HierarchyLoadingState />;
  }

  if (!hasItems) {
    return <EmptyState />;
  }

  return <>{children}</>;
};

interface SidebarNewItemPopoverContentProps {
  isLoading: boolean;
  loadingType: EntityType | null;
  isCreateDisabled?: (type: EntityType) => boolean;
  onItemClick: (type: NewItemEntityType) => Promise<void>;
}

const SidebarNewItemPopoverContent = ({
  isLoading,
  loadingType,
  isCreateDisabled,
  onItemClick,
}: SidebarNewItemPopoverContentProps) => (
  <PopoverContent
    className="w-80 rounded-xl border border-gray-200 bg-white p-2 shadow-lg"
    side="right"
    align="start"
    sideOffset={8}
  >
    <NewItemPopoverContent
      groups={ITEM_TYPE_GROUPS}
      onItemClick={type => {
        void onItemClick(type);
      }}
      isCreateDisabled={isCreateDisabled}
      isLoading={isLoading}
      loadingType={loadingType}
    />
  </PopoverContent>
);

/** Inline "New item" button rendered inside the tree for empty expanded folders. */
interface InlineNewItemButtonProps {
  depth: number;
  parentFolderId?: number;
}

const InlineNewItemButton = ({ depth, parentFolderId }: InlineNewItemButtonProps) => {
  const [open, setOpen] = useState(false);
  const { actions, isLoading, loadingType, smartTableCreateDialogProps } =
    useNewItemActions(parentFolderId);
  const { isCreateDisabled } = useRbac();
  const handleItemClick = async (type: NewItemEntityType) => {
    await actions[type]?.();
    setOpen(false);
  };

  return (
    <>
      <SmartTableCreateDialog {...smartTableCreateDialogProps} />
      <Popover
        open={open}
        onOpenChange={nextOpen => {
          if (!isLoading) {
            setOpen(nextOpen);
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            disabled={isLoading}
            className={cn(
              "group flex w-full cursor-pointer select-none items-center rounded-md px-2 py-1 text-sm transition-all duration-200",
              "text-gray-400 hover:bg-gray-50 hover:text-gray-500 disabled:cursor-not-allowed disabled:opacity-60",
            )}
            style={{ paddingLeft: `${8 + depth * 18}px` }}
          >
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              {isLoading ? (
                <LoadingSpinner size={4} className="!mr-0" />
              ) : (
                <PlusIcon className="h-3.5 w-3.5 flex-shrink-0" />
              )}
              <span className="text-[13px]">{isLoading ? "Creating..." : "New item"}</span>
            </div>
          </button>
        </PopoverTrigger>
        <SidebarNewItemPopoverContent
          isLoading={isLoading}
          loadingType={loadingType}
          isCreateDisabled={isCreateDisabled}
          onItemClick={handleItemClick}
        />
      </Popover>
    </>
  );
};

interface FileHierarchyTreeProps {
  className?: string;
}

/** Inner component, lives inside <UnifiedRegistryProvider> so it can read selection state. */
const FileHierarchyTree = observer(({ className }: FileHierarchyTreeProps) => {
  const params = useParams();
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const virtualContainerRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const [favoritesStickyHeight, setFavoritesStickyHeight] = useState(0);
  const [newItemOpen, setNewItemOpen] = useState(false);
  const authContext = useAuth();
  const registry = useUnifiedRegistry();

  const handleFavoritesStickyHeightChange = useCallback((height: number) => {
    setFavoritesStickyHeight(height);
  }, []);

  const {
    expandedFolders,
    toggleFolder,
    collapseAllFolders,
    rootItems,
    isLoading,
    getChildren,
    allEntities,
  } = useFileHierarchy();

  const moveMutation = useMoveFolderEntity(authContext?.userToken ?? "");

  const handleMove = useCallback(
    async (item: RegistryItem, targetFolderId: number | null) => {
      if (targetFolderId === item.folder_id) {
        return;
      }

      const isItemSelected = registry.isSelected(item);
      const entities =
        isItemSelected && registry.selectedEntities.length > 0 ? registry.selectedEntities : [item];

      if (
        targetFolderId !== null &&
        entities.some(
          e => e.type === EntityType.FOLDER && getEntityNumericId(e.id) === targetFolderId,
        )
      ) {
        return;
      }

      try {
        await moveMutation.mutateAsync({ entities, folder_id: targetFolderId });

        const targetFolder =
          targetFolderId !== null
            ? allEntities.find(
                e => getEntityNumericId(e.id) === targetFolderId && e.type === EntityType.FOLDER,
              )
            : null;
        const destination = targetFolderId === null ? "Home" : targetFolder?.name || "folder";

        const isBulk = isItemSelected && entities.length > 1;
        const toastMessage = isBulk
          ? `${entities.length} items moved to ${destination}`
          : `${item.name || "Item"} moved to ${destination}`;

        if (isItemSelected) {
          registry.clearSelection();
        }
        displayToast(toastMessage, ToastType.success);
      } catch {
        // Surfaced by the mutation's onError handler
      }
    },
    [moveMutation, allEntities, registry],
  );

  const folderHierarchy = useMemo(
    () =>
      allEntities.reduce<{ id: number; folder_id: number | null }[]>((folders, entity) => {
        if (entity.type !== EntityType.FOLDER) {
          return folders;
        }

        const folderId = getEntityNumericId(entity.id);
        if (folderId === undefined) {
          return folders;
        }

        folders.push({ id: folderId, folder_id: entity.folder_id ?? null });
        return folders;
      }, []),
    [allEntities],
  );

  const allRegistryItems = allEntities as RegistryItem[];

  useSidebarSelectionKeyboard(allRegistryItems);

  const { navigate, getItemPath, isCurrentPath, getItemIcon } = useTreeNavigation();

  const currentFolderId = useMemo(() => {
    if (params.folderId) {
      return parseInt(params.folderId, 10);
    }

    // Determine the active entity from any route param
    const entityLookup: { type: EntityType; id: number | string } | null = (() => {
      if (params.promptId) {
        return { type: EntityType.PROMPT, id: Number(params.promptId) };
      }
      if (params.workflowId) {
        return { type: EntityType.WORKFLOW, id: Number(params.workflowId) };
      }
      if (params.datasetGroupId) {
        return { type: EntityType.DATASET, id: Number(params.datasetGroupId) };
      }
      if (params.blueprintId) {
        return { type: EntityType.REPORT, id: Number(params.blueprintId) };
      }
      if (params.releaseLabelGroupId) {
        return { type: EntityType.AB_TEST, id: Number(params.releaseLabelGroupId) };
      }
      if (params.setId) {
        return { type: EntityType.INPUT_VARIABLE_SET, id: Number(params.setId) };
      }
      if (params.smartTableId) {
        return { type: EntityType.SMART_TABLE, id: params.smartTableId };
      }
      if (params.snippetId) {
        return { type: EntityType.SNIPPET, id: Number(params.snippetId) };
      }
      return null;
    })();

    if (!entityLookup) {
      return undefined;
    }

    // Look up the entity in the full tree to find its parent folder
    const entity = allEntities.find(
      e => String(e.id) === String(entityLookup.id) && e.type === entityLookup.type,
    );
    return entity?.folder_id ?? undefined;
  }, [params, allEntities]);

  const {
    actions,
    isLoading: isCreatingNewItem,
    loadingType: creatingNewItemType,
    smartTableCreateDialogProps,
  } = useNewItemActions(currentFolderId);
  const { isCreateDisabled } = useRbac();
  const handleNewItemClick = async (type: NewItemEntityType) => {
    await actions[type]?.();
    setNewItemOpen(false);
  };

  const homePath = `/workspace/${params.workspaceId}/home`;
  const isHomeActive = location.pathname === homePath;

  const [isFavoritesExpanded, setIsFavoritesExpanded] = useLocalStorageState(
    LOCAL_STORAGE_KEYS.SIDEBAR_FAVORITES_EXPANDED,
    true,
  );
  const [isHomeExpanded, setIsHomeExpanded] = useLocalStorageState(
    LOCAL_STORAGE_KEYS.SIDEBAR_HOME_EXPANDED,
    true,
  );

  useEffect(() => {
    const openFavorites = () => setIsFavoritesExpanded(true);
    window.addEventListener(SIDEBAR_FAVORITES_EXPAND_EVENT, openFavorites);
    return () => window.removeEventListener(SIDEBAR_FAVORITES_EXPAND_EVENT, openFavorites);
  }, [setIsFavoritesExpanded]);

  const handleHomeNavigate = useCallback(() => {
    navigate(homePath);
  }, [navigate, homePath]);

  const toggleFavorites = useCallback(() => {
    setIsFavoritesExpanded(prev => !prev);
  }, [setIsFavoritesExpanded]);

  const toggleHome = useCallback(() => {
    setIsHomeExpanded(prev => !prev);
  }, [setIsHomeExpanded]);

  const { virtualizer, flattenedItems } = useVirtualizedTree({
    rootItems,
    expandedFolders,
    getChildren,
    parentRef: scrollContainerRef,
    estimateSize: 32,
    scrollMargin,
  });

  // Favorites (+ Home header) sit above the Home virtual list in the same scroll
  // container. TanStack Virtual needs that offset as scrollMargin; if it drifts
  // (common with sticky headers), rows render too low and leave a blank gap.
  useLayoutEffect(() => {
    const scrollEl = scrollContainerRef.current;
    const virtualEl = virtualContainerRef.current;
    if (!scrollEl || !virtualEl) {
      return undefined;
    }

    const update = () => {
      const offset = getOffsetRelativeToAncestor(virtualEl, scrollEl);
      setScrollMargin(prev => (prev === offset ? prev : offset));
    };

    const resizeObserver = new ResizeObserver(update);
    const observeLayout = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(scrollEl);
      resizeObserver.observe(virtualEl);
      Array.from(scrollEl.children).forEach(child => {
        resizeObserver.observe(child);
      });
    };

    observeLayout();
    update();

    // Favorites mounts/unmounts as a direct child when the list is empty/non-empty.
    const mutationObserver = new MutationObserver(() => {
      observeLayout();
      update();
    });
    mutationObserver.observe(scrollEl, { childList: true });

    // Radix collapsible height animation settles after paint.
    const rafId = window.requestAnimationFrame(() => {
      update();
      window.requestAnimationFrame(update);
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [
    isFavoritesExpanded,
    isHomeExpanded,
    flattenedItems.length,
    rootItems.length,
    favoritesStickyHeight,
  ]);

  const lastVisibleParentId = useMemo<number | null>(() => {
    if (flattenedItems.length === 0) {
      return null;
    }
    const last = flattenedItems[flattenedItems.length - 1];
    if (last.isCreatePlaceholder) {
      return last.parentFolderId ?? null;
    }
    return last.entity.folder_id ?? null;
  }, [flattenedItems]);

  const handleItemNavigate = useCallback(
    (item: Entity) => {
      const path = getItemPath(item);
      navigate(path);
    },
    [getItemPath, navigate],
  );

  return (
    <>
      <SmartTableCreateDialog {...smartTableCreateDialogProps} />
      <DragDropProvider onMove={handleMove} folderHierarchy={folderHierarchy}>
        <div
          className={cn(
            "flex h-full flex-col px-2 py-3 transition-all duration-200 ease-in-out",
            className,
          )}
        >
          <div
            ref={scrollContainerRef}
            data-scroll-container
            className="flex-1 overflow-y-auto px-2 [overflow-anchor:none]"
          >
            <FavoritesSection
              isExpanded={isFavoritesExpanded}
              onToggle={toggleFavorites}
              onStickyHeaderHeightChange={handleFavoritesStickyHeightChange}
            />

            {/* Sticky headers must be direct children of the scroll container so they
                remain stuck for the full scroll range (not trapped in short Collapsibles). */}
            <HomeRow
              isActive={isHomeActive}
              isExpanded={isHomeExpanded}
              onToggle={toggleHome}
              onNavigate={handleHomeNavigate}
              onCollapseAllFolders={collapseAllFolders}
              hasExpandedFolders={expandedFolders.size > 0}
              stickyTopPx={favoritesStickyHeight}
            />

            <Collapsible open={isHomeExpanded}>
              <CollapsibleContent>
                {/* Tree content indented under Home */}
                <div className={`relative ${rootItems.length > 0 ? "ml-[15px]" : ""}`}>
                  <HierarchyContent isLoading={isLoading} hasItems={rootItems.length > 0}>
                    <div
                      ref={virtualContainerRef}
                      className="relative py-1 [overflow-anchor:none]"
                      style={{
                        height: `${virtualizer.getTotalSize()}px`,
                      }}
                    >
                      {virtualizer.getVirtualItems().map(virtualItem => {
                        const flatItem = flattenedItems[virtualItem.index];
                        if (!flatItem) {
                          return null;
                        }

                        // Render inline "New item" for empty expanded folders
                        if (flatItem.isCreatePlaceholder) {
                          return (
                            <div
                              key={virtualItem.key}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: `${virtualItem.size}px`,
                                transform: `translateY(${virtualItem.start - scrollMargin}px)`,
                              }}
                            >
                              <InlineNewItemButton
                                depth={flatItem.depth}
                                parentFolderId={flatItem.parentFolderId ?? undefined}
                              />
                            </div>
                          );
                        }

                        const itemPath = getItemPath(flatItem.entity);
                        const isActive = isCurrentPath(itemPath);
                        const folderId =
                          flatItem.entity.type === EntityType.FOLDER
                            ? getEntityNumericId(flatItem.entity.id)
                            : undefined;
                        const isExpanded = folderId !== undefined && expandedFolders.has(folderId);
                        const children = folderId !== undefined ? getChildren(folderId) : [];
                        const hasChildren = children.length > 0;

                        return (
                          <VirtualizedTreeItem
                            key={virtualItem.key}
                            virtualItem={virtualItem}
                            entity={flatItem.entity}
                            depth={flatItem.depth}
                            isActive={isActive}
                            isExpanded={isExpanded}
                            onToggleFolder={toggleFolder}
                            onNavigate={handleItemNavigate}
                            getItemIcon={getItemIcon}
                            hasChildren={hasChildren}
                            scrollMargin={scrollMargin}
                          />
                        );
                      })}
                    </div>
                  </HierarchyContent>
                </div>

                {rootItems.length > 0 && <TrailingDropZone parentFolderId={lastVisibleParentId} />}

                {/* New item CTA */}
                <div className="py-1">
                  <Popover
                    open={newItemOpen}
                    onOpenChange={nextOpen => {
                      if (!isCreatingNewItem) {
                        setNewItemOpen(nextOpen);
                      }
                    }}
                  >
                    <PopoverTrigger asChild>
                      <button
                        disabled={isCreatingNewItem}
                        className={cn(
                          "group flex w-full cursor-pointer select-none items-center rounded-md px-2 py-1 text-sm transition-all duration-200",
                          "text-gray-400 hover:bg-gray-50 hover:text-gray-500 disabled:cursor-not-allowed disabled:opacity-60",
                        )}
                        style={{ paddingLeft: "8px" }}
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-1.5">
                          {isCreatingNewItem ? (
                            <LoadingSpinner size={4} className="!mr-0" />
                          ) : (
                            <PlusIcon className="h-3.5 w-3.5 flex-shrink-0" />
                          )}
                          <span className="text-[13px]">
                            {isCreatingNewItem ? "Creating..." : "New item"}
                          </span>
                        </div>
                      </button>
                    </PopoverTrigger>
                    <SidebarNewItemPopoverContent
                      isLoading={isCreatingNewItem}
                      loadingType={creatingNewItemType}
                      isCreateDisabled={isCreateDisabled}
                      onItemClick={handleNewItemClick}
                    />
                  </Popover>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </DragDropProvider>
    </>
  );
});

FileHierarchyTree.displayName = "FileHierarchyTree";

const FileHierarchy = ({ className }: FileHierarchyProps) => {
  const params = useParams();
  const location = useLocation();
  const { navigate } = useTreeNavigation();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  const homePath = `/workspace/${params.workspaceId}/home`;
  const isHomeActive = location.pathname === homePath;

  const handleHomeNavigate = useCallback(() => {
    navigate(homePath);
  }, [navigate, homePath]);

  if (isCollapsed) {
    return (
      <div className={cn("flex flex-col px-2 py-3", className)}>
        <div className="flex justify-center">
          <button
            onClick={handleHomeNavigate}
            className={cn(
              "flex h-10 w-10 items-center justify-center rounded-md transition-all duration-150",
              isHomeActive
                ? "bg-blue-50 text-blue-500"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
            )}
            title="Home"
          >
            <HomeIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <UnifiedRegistryProvider>
      <FileHierarchyTree className={className} />
    </UnifiedRegistryProvider>
  );
};

export default FileHierarchy;
